import { dataReadText, providerReasonText, refusalText } from "./refusal-text.js";
import { STRATEGY } from "./strategy.js";
const ACTION_LABEL = {
    retire: "Retiring the failed opening",
    cleanup: "Cleaning up the failed opening",
    "profit-stop": "Arming the profit stop",
};
const LOW_YIELD_CLOSED = `Closed: low fee yield (under ${STRATEGY.exits.lowYieldExitPercentPerDay} %/day after ${String(STRATEGY.exits.lowYieldExitAfterSeconds / 86_400)} days)`;
/**
 * One sentence per entry kind, carrying the number that justifies the decision (design 47,
 * section 5). Shared by the web journal and the Telegram bot; every surface-specific bit of
 * formatting (money, relative/absolute time) comes through `fmt` so the two stay byte-identical.
 */
export function journalEntryText(facts, fmt) {
    switch (facts.kind) {
        case "started":
            return `Started with ${fmt.money(facts.capital)}${facts.capitalMode === "whole" ? " (whole balance)" : ""} · drawdown ${facts.limits.drawdownPercent}% · ${String(facts.limits.entriesPer24h)} entries/24h`;
        case "paused":
            return facts.mode === "close"
                ? "Paused and closed the position"
                : "Paused, kept the position";
        case "stopping":
            return `Stopping (${facts.mode === "close" ? "close on finish" : "keep on finish"})`;
        case "resumed":
            return facts.peak !== null
                ? `Resumed · peak re-based to ${fmt.money(facts.peak)}`
                : "Resumed";
        case "limits-changed":
            return `Limits changed · drawdown ${facts.limits.drawdownPercent}% · ${String(facts.limits.entriesPer24h)} entries/24h`;
        case "released":
            return facts.capital !== null
                ? `Released to the operator · capital now ${fmt.money(facts.capital)}`
                : "Released to the operator";
        case "restored":
            return "Restored after a backup restore";
        case "pool-picked":
            return `Pool picked, rank ${String(facts.rank)}${facts.lpFeesTvlPercent !== null ? ` · ${facts.lpFeesTvlPercent}% fees/TVL` : ""}`;
        case "no-candidate":
            return `No candidate among ${String(facts.ranked)} ranked · ranks again ${fmt.when(facts.retryAt)}`;
        case "not-entering":
            return `Not entering · ${refusalText(facts.refusal)}`;
        case "opened":
            return `Opened (attempt ${String(facts.attempt)}) · ${facts.funding !== null ? fmt.money(facts.funding) : "amount unavailable"}`;
        case "open-retry":
            return `Opening retry ${String(facts.attempt)} of ${String(facts.maxAttempts)}${facts.failureCode ? ` · ${facts.failureCode}` : ""}`;
        case "open-failed":
            return `Opening given up after ${String(facts.attempts)} attempts${facts.retryAt ? ` · waits until ${fmt.when(facts.retryAt)}` : ""}`;
        case "profit-stop-armed":
            return `Profit stop armed${facts.resultPercent !== null ? ` · net ${facts.resultPercent}%` : ""}`;
        case "closed":
            return `${facts.closeKind === "low-yield" ? LOW_YIELD_CLOSED : `Closed (${facts.closeKind})`}${facts.returned !== null ? ` · returned ${fmt.money(facts.returned)}` : ""}${facts.resultPercent !== null ? ` · ${facts.resultPercent}%` : ""}`;
        case "capital-realized":
            return `Capital realized · ${fmt.money(facts.capital)}`;
        case "sold-back":
            // A funding-token amount, never a dollar figure: no `fmt.money`.
            return `Deposit failed after the swap, sold back · cost ${facts.cost !== null ? `${facts.cost} ${facts.symbol}`.trim() : "unavailable"}`;
        case "capital-transfer": {
            const sign = facts.direction === "added" ? "+" : "−";
            return `${sign}${fmt.money(facts.amount)} ${facts.direction} · peak moved to ${fmt.money(facts.peak)}`;
        }
        case "capital-mode-changed":
            return `Capital mode changed to ${facts.capitalMode === "whole" ? "the whole free balance" : "a fixed amount"} · capital ${fmt.money(facts.capital)} · peak ${fmt.money(facts.peak)}`;
        case "close-retry":
            return `Close retry ${String(facts.attempt)}${facts.nextAttemptAt ? ` · next ${fmt.when(facts.nextAttemptAt)}` : ""}`;
        case "action-retry":
            return `${ACTION_LABEL[facts.action]} refused (${String(facts.attempt)}) · ${facts.failureCode} · next ${fmt.when(facts.nextAttemptAt)}`;
        case "limit-reached":
            return `Limit reached: ${facts.limit}${facts.value !== null ? ` · ${facts.value} vs ${facts.threshold}` : ` · threshold ${facts.threshold}`}${facts.freesAt ? ` · frees ${fmt.when(facts.freesAt)}` : ""}`;
        case "limit-freed":
            return "Entries limit freed";
        case "needs-attention":
            return `Needs attention: ${facts.cause}`;
        case "attention-cleared":
            return `Attention cleared: ${facts.cause}`;
        case "on-hold":
            return `On hold: ${facts.hold}`;
        case "hold-cleared":
            return "Hold cleared";
        case "valuation-unavailable":
            return "Valuation unavailable";
        case "data-unavailable": {
            // A scan error carries the server's sanitized error text, not a provider reason code.
            if (facts.read === "scan")
                return `Cannot check the pools: ${dataReadText(facts.read)} failed${facts.reason !== null ? ` (${facts.reason})` : ""} — ${String(facts.attempts)} ${facts.attempts === 1 ? "attempt" : "attempts"} in ${String(facts.minutes)} min; not entering another pool, retrying`;
            const reason = providerReasonText(facts.reason);
            return `Cannot check ${dataReadText(facts.read)}: unavailable${reason ? ` (${reason})` : ""} — ${String(facts.attempts)} attempts in ${String(facts.minutes)} min; not entering another pool, retrying`;
        }
        case "data-available":
            return `Pool checks work again after ${String(facts.minutes)} min`;
        case "outcome-dropped":
            return `Result of ${facts.action} ignored · position ${String(facts.cycleNo)} already ended`;
        case "week-loss": {
            const week = facts.weekReturnPercent !== null ? ` · week ${facts.weekReturnPercent}%` : "";
            return facts.half ? `Entries halved${week}` : `Entries back to full size${week}`;
        }
    }
}
